import React, { useState, useRef, useEffect } from "react";
import { useSeo } from "../seo";
import { Plus, Minus } from "lucide-react";
import { DynamicNavigation, Footer } from "../components";
import CTA02 from "../components/CTA02Section";
import { useNavLinkClick } from "../hooks/useNavLinkClick";

/* ─── Data ─────────────────────────────────────────────────────────────── */

const visaCards = [
  {
    subclass: "Subclass 482",
    title: "Temporary Skill Shortage Visa",
    desc: "Allows approved Australian employers to sponsor skilled overseas workers when suitable local candidates cannot be found. Ideal for businesses needing specialist talent or urgent workforce support.",
    points: [
      "Temporary skilled hiring",
      "Access global talent",
      "Fill critical skill shortages",
      "Employer sponsorship pathway",
    ],
    color: "indigo",
    note: null,
    url: "/employer-sponsored-visas-australia",
  },
  {
    subclass: "Subclass 186",
    title: "Employer Nomination Scheme Visa",
    desc: "Provides a pathway for eligible skilled workers to obtain permanent residence through employer nomination. Suitable for businesses hiring long-term staff in strategic roles.",
    points: [
      "Permanent residence pathway",
      "Retain valuable employees",
      "Long-term workforce planning",
    ],
    color: "blue",
    note: null,
    url: "/employer-sponsored-visas-australia",
  },
  {
    subclass: "Subclass 417 / 462",
    title: "Working Holiday & Work and Holiday Visa",
    desc: "Allow eligible young travellers to visit Australia and undertake short-term work while travelling. Commonly used in tourism, hospitality, agriculture, and seasonal sectors.",
    points: [
      "Temporary workforce support",
      "Flexible hiring",
      "Seasonal demand coverage",
    ],
    color: "amber",
    note: "These are not employer-sponsored visas.",
    url: "/contact",
  },
];

const colorMap: Record<string, { badge: string; accent: string; dot: string }> = {
  indigo: { badge: "text-indigo-600 bg-indigo-50 border-indigo-100", accent: "from-indigo-500 to-blue-500", dot: "#4f46e5" },
  blue:   { badge: "text-blue-600 bg-blue-50 border-blue-100", accent: "from-blue-500 to-cyan-500", dot: "#2563eb" },
  amber:  { badge: "text-amber-600 bg-amber-50 border-amber-100", accent: "from-amber-500 to-orange-400", dot: "#d97706" },
};

const processSteps = [
  {
    step: "01",
    title: "Assess Workforce Needs",
    desc: "Identify skill shortages, urgent vacancies, and future staffing requirements. Understanding what roles cannot be filled locally is the essential starting point.",
    image: "/assets/services-cards/images/corporate-immigration.jpg",
  },
  {
    step: "02",
    title: "Select the Right Visa Pathway",
    desc: "Choose the most suitable visa based on the role type, employment duration, skill level, and overall business goals. Each visa has different eligibility criteria.",
    image: "/assets/services-cards/images/employer-sponsored-visas.jpg",
  },
  {
    step: "03",
    title: "Sponsorship & Nomination",
    desc: "Where required, the business applies for sponsorship approval and nominates an eligible role that meets occupation and salary requirements.",
    image: "/assets/services-cards/images/skilled-visas.jpg",
  },
  {
    step: "04",
    title: "Worker Application",
    desc: "The overseas professional submits their visa application with all required documentation including qualifications, experience, health, and character evidence.",
    image: "/assets/services-cards/images/skills-assessment.jpg",
  },
  {
    step: "05",
    title: "Onboarding & Compliance",
    desc: "Once the visa is approved, employment begins. Businesses must maintain ongoing visa and workplace compliance obligations throughout the employment period.",
    image: "/assets/services-cards/images/family-partner-visas.jpg",
  },
];

const employerResponsibilities = [
  "Apply to become an approved standard business sponsor where required",
  "Nominate eligible occupations that meet Department of Home Affairs requirements",
  "Meet minimum salary thresholds in line with market salary rates",
  "Provide employment conditions consistent with Australian workplace standards",
  "Maintain ongoing compliance with workplace laws and Fair Work obligations",
  "Keep required records relating to sponsored workers",
  "Notify the Department of Home Affairs of relevant changes to employment arrangements",
  "Cover certain costs related to sponsored workers where applicable",
];

const applicantRequirements = [
  { label: "Relevant qualifications and educational credentials" },
  { label: "Demonstrated work experience in the nominated occupation" },
  { label: "Occupation-specific skills and competencies" },
  { label: "English language proficiency (varies by visa type)" },
  { label: "Health examinations as required" },
  { label: "Character requirements and police clearances" },
  { label: "Skills assessment from a recognised assessing authority (where required)" },
];

const industries = [
  {
    label: "Healthcare",
    bg: "bg-violet-100",
    iconColor: "#7F22FE",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 5v14M5 12h14" stroke="#7F22FE" strokeWidth="2" strokeLinecap="round" />
        <rect x="2" y="2" width="20" height="20" rx="4" stroke="#7F22FE" strokeWidth="1.8" />
      </svg>
    ),
  },
  {
    label: "Aged Care",
    bg: "bg-pink-100",
    iconColor: "#DB2777",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="#DB2777" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Construction",
    bg: "bg-orange-100",
    iconColor: "#F54900",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M3 22h18M4 22V11l8-7 8 7v11" stroke="#F54900" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="9" y="15" width="6" height="7" rx="1" stroke="#F54900" strokeWidth="1.8" />
      </svg>
    ),
  },
  {
    label: "Engineering",
    bg: "bg-green-100",
    iconColor: "#00A63E",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z" stroke="#00A63E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Information Technology",
    bg: "bg-blue-100",
    iconColor: "#2563EB",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="5" width="20" height="14" rx="2" stroke="#2563EB" strokeWidth="1.8" />
        <path d="M8 10l-2 2 2 2M16 14l2-2-2-2M13 9l-2 6" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Hospitality",
    bg: "bg-yellow-100",
    iconColor: "#CA8A04",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8zM6 1v3M10 1v3M14 1v3" stroke="#CA8A04" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Agriculture",
    bg: "bg-lime-100",
    iconColor: "#65A30D",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 22V12M12 12C12 7 8 4 3 3c0 5 3 9 7 9h2zM12 12c0-5 4-8 9-9 0 5-3 9-7 9h-2z" stroke="#65A30D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Logistics",
    bg: "bg-cyan-100",
    iconColor: "#0891B2",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="1" y="3" width="15" height="13" rx="1" stroke="#0891B2" strokeWidth="1.8" />
        <path d="M16 8h4l3 3v5h-7V8z" stroke="#0891B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5.5" cy="18.5" r="2.5" stroke="#0891B2" strokeWidth="1.8" />
        <circle cx="18.5" cy="18.5" r="2.5" stroke="#0891B2" strokeWidth="1.8" />
      </svg>
    ),
  },
  {
    label: "Education",
    bg: "bg-indigo-100",
    iconColor: "#4338CA",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 3L2 9l10 6 10-6-10-6z" stroke="#4338CA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 12.5v5C7 20 9.686 22 12 22s5-2 5-4.5v-5" stroke="#4338CA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Manufacturing",
    bg: "bg-slate-100",
    iconColor: "#475569",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M2 20h20M4 20V10l4-4 4 4 4-8 4 8v10" stroke="#475569" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const supportServices = [
  "Workforce migration strategy",
  "Visa pathway guidance",
  "Employer sponsorship support",
  "Eligibility checks",
  "Document preparation",
  "Application guidance",
  "Compliance awareness",
  "Ongoing updates",
];

const faqs = [
  {
    q: "What is corporate immigration in Australia?",
    a: "Corporate immigration refers to the migration pathways and processes that enable Australian businesses to hire skilled overseas workers. It covers employer-sponsored visa categories, sponsorship obligations, nomination processes, and ongoing compliance requirements when employing international professionals.",
  },
  {
    q: "How can businesses hire overseas workers in Australia?",
    a: "Businesses can hire overseas workers through employer-sponsored visa pathways such as the Temporary Skill Shortage Visa (Subclass 482) or the Employer Nomination Scheme Visa (Subclass 186). The process generally involves becoming an approved sponsor, nominating a suitable position, and supporting the worker's visa application.",
  },
  {
    q: "What is the difference between 482 and 186 visas?",
    a: "The Subclass 482 visa is a temporary visa allowing businesses to sponsor skilled workers for a defined period. The Subclass 186 visa offers a pathway to permanent residence for eligible skilled workers nominated by an employer. The 186 is generally suited to long-term or permanent hiring needs, while the 482 addresses short-to-medium-term skill gaps.",
  },
  {
    q: "Do companies need sponsorship approval to hire overseas workers?",
    a: "Yes. To hire workers on employer-sponsored visas such as the Subclass 482, businesses must first be approved as a standard business sponsor by the Department of Home Affairs. Sponsorship approval requires meeting certain eligibility and compliance requirements.",
  },
  {
    q: "Which industries use overseas workers most?",
    a: "Industries that commonly rely on overseas workers in Australia include healthcare, aged care, information technology, engineering, construction, hospitality, agriculture, education, logistics, and manufacturing. These sectors frequently experience skill shortages that are addressed through international recruitment.",
  },
  {
    q: "Can overseas workers get permanent residency in Australia?",
    a: "Yes. Certain employer-sponsored visa pathways provide routes to permanent residence. The Employer Nomination Scheme Visa (Subclass 186) is one example of a pathway that may lead to permanent residence for eligible sponsored workers, depending on the employer's nomination and the applicant meeting relevant criteria.",
  },
];

/* ─── VisaCard sub-component ─────────────────────────────────────────────── */

const CorpVisaCard = ({
  subclass, title, desc, points, color, note, url,
}: (typeof visaCards)[0]) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const divRef = useRef<HTMLDivElement>(null);
  const c = colorMap[color];

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!divRef.current) return;
    const bounds = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      className="relative rounded-xl p-0.5 bg-white overflow-hidden shadow-md cursor-pointer w-full"
    >
      {visible && (
        <div
          className="pointer-events-none blur-xl bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 size-48 absolute z-0 transition-opacity duration-300"
          style={{ top: position.y - 96, left: position.x - 96 }}
        />
      )}
      <div className="relative z-10 bg-white p-6 h-full w-full rounded-[10px] flex flex-col">
        <span className={`inline-block text-xs font-semibold border rounded-full px-3 py-1 mb-3 w-max ${c.badge}`}>
          {subclass}
        </span>
        <h3 className="text-base font-bold text-gray-800 mb-2 leading-snug">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-4">{desc}</p>
        <ul className="space-y-2 mt-auto mb-4">
          {points.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <span className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: `${c.dot}18` }}>
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke={c.dot} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {p}
            </li>
          ))}
        </ul>
        {note && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            ⚠ {note}
          </p>
        )}
      </div>
    </div>
  );
};

/* ─── Page ──────────────────────────────────────────────────────────────── */

export default function CorporateImmigrationServicesAustralia() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<"next" | "prev">("next");
  const [animating, setAnimating] = useState(false);

  const navigate = (dir: "next" | "prev") => {
    if (animating) return;
    const next =
      dir === "next"
        ? (activeStep + 1) % processSteps.length
        : (activeStep - 1 + processSteps.length) % processSteps.length;
    setSlideDir(dir);
    setPrevStep(activeStep);
    setActiveStep(next);
    setAnimating(true);
    setTimeout(() => { setPrevStep(null); setAnimating(false); }, 540);
  };

  const goTo = (idx: number) => {
    if (idx === activeStep || animating) return;
    setSlideDir(idx > activeStep ? "next" : "prev");
    setPrevStep(activeStep);
    setActiveStep(idx);
    setAnimating(true);
    setTimeout(() => { setPrevStep(null); setAnimating(false); }, 540);
  };

  useEffect(() => {
    const t = setInterval(() => navigate("next"), 3500);
    return () => clearInterval(t);
  }, [activeStep, animating]);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];
  const onLinkClick = useNavLinkClick(links);

  useSeo({
    title: "Corporate Immigration Services Australia | Business Visa & Workforce Solutions | Hola World Migration",
    description:
      "Help your business hire skilled overseas workers in Australia. Explore corporate immigration services, 482 visa, 186 visa, workforce migration pathways, and expert support from Hola World Migration.",
    path: "/corporate-immigration-services-australia",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Corporate Immigration Services Australia", url: "/corporate-immigration-services-australia" },
    ],
    faqItems: faqs.map((f) => ({ question: f.q, answer: f.a })),
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Hola World Migration" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      <main className="w-full flex flex-col items-center max-w-full">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="bg-gradient-to-b from-[#F5F7FF] via-[#fffbee] to-[#E6EFFF] w-full font-poppins">
          <div className="flex flex-col md:flex-row items-center max-md:text-center justify-between mt-24 pb-16 px-6 sm:px-10 md:px-24 max-w-7xl mx-auto w-full gap-8">
            {/* Left */}
            <div className="flex flex-col items-center md:items-start">
              <button
                type="button"
                className="mt-16 mb-6 flex items-center gap-2 border border-indigo-600 text-indigo-600 text-xs rounded-full px-4 py-1.5 hover:bg-indigo-50 transition"
              >
                <span>Corporate Migration Solutions · Australia</span>
              </button>
              <h1
                className="text-gray-900 font-semibold text-4xl sm:text-5xl md:text-6xl max-w-xl leading-tight"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Corporate Immigration Services{" "}
                <span className="text-indigo-600">Australia</span>
              </h1>
              <p className="mt-4 text-gray-600 max-w-md text-sm sm:text-base leading-relaxed">
                Support your business growth by accessing skilled international talent through the right migration pathways.
              </p>
              <p className="mt-3 text-gray-500 max-w-md text-sm leading-relaxed">
                Australian businesses often rely on overseas professionals to address workforce shortages and strengthen their teams.
              </p>

            </div>

            {/* Right: 2×2 image grid */}
            <div aria-label="Corporate immigration images" className="mt-12 md:mt-0 grid grid-cols-2 gap-6 pb-6 shrink-0">
              <img alt="Corporate Immigration Australia" src="/assets/services-cards/images/corporate-immigration.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Employer Sponsored Visas" src="/assets/services-cards/images/employer-sponsored-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Skilled Visas Australia" src="/assets/services-cards/images/skilled-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Skills Assessment" src="/assets/services-cards/images/skills-assessment.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
            </div>
          </div>
        </section>

        {/* ── SECTION 1: CORPORATE IMMIGRATION OVERVIEW ────────────────── */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/corporate-immigration.jpg"
                alt="Corporate Immigration Services Australia"
              />
            </div>
            <div className="w-full max-w-lg">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Corporate Immigration
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6" />
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Corporate immigration covers migration pathways that help Australian businesses hire skilled overseas professionals. In response to labour shortages and specialised workforce demands, many organisations across industries turn to international talent to fill critical roles, expand operations, and remain competitive.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Australia offers several visa pathways that may allow businesses to employ overseas workers depending on the occupation, skill requirements, and employment duration.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Understanding these options and associated responsibilities helps businesses plan effectively, reduce hiring delays, and meet immigration requirements when recruiting global talent.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                New businesses entering the Australian migration system may benefit from understanding how visa categories align with workforce planning and long-term recruitment strategies.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECTION 2: CORPORATE VISA PATHWAYS ───────────────────────── */}
        <section className="w-full bg-blue-50 py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Corporate Visa Pathways
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-3 max-w-3xl">
              Several visa options may support businesses that need to recruit or employ overseas workers.
            </p>
            <p className="text-sm text-slate-400 mb-10">
              Choose the pathway that best suits your role type, employment duration, and business objectives.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visaCards.map((card, i) => (
                <CorpVisaCard key={i} {...card} />
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 3: PROCESS STEPPER ───────────────────────────────── */}
        <section className="w-full relative overflow-hidden" style={{ background: "#1B31F5" }}>
          <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 pt-16 pb-0 relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-end pr-4 sm:pr-20 pointer-events-none select-none z-0 overflow-hidden"
            >
              <span
                key={`num-${activeStep}`}
                className="animate-process-num text-[320px] sm:text-[420px] md:text-[520px] font-black leading-none"
                style={{ color: "rgba(255,255,255,0.13)", letterSpacing: "-0.04em" }}
              >
                {processSteps[activeStep].step}
              </span>
            </div>

            <h2
              className="relative z-10 text-white font-bold text-3xl sm:text-4xl md:text-5xl text-center mb-12"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Corporate Immigration Process
            </h2>

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              <div
                key={activeStep}
                className={`flex-1 max-w-lg ${prevStep !== null ? (slideDir === "next" ? "proc-step-enter-right" : "proc-step-enter-left") : ""}`}
              >
                <p className="text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Step {processSteps[activeStep].step}
                </p>
                <h3 className="text-white font-bold text-3xl sm:text-4xl mb-4 leading-snug">
                  {processSteps[activeStep].title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10">
                  {processSteps[activeStep].desc}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("prev")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate("next")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Next step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-shrink-0 flex items-end justify-center lg:justify-end mb-14">
                <div className="relative w-80 h-64 sm:w-[440px] sm:h-80">
                  {prevStep !== null && prevStep < processSteps.length && (
                    <img
                      src={processSteps[prevStep].image}
                      alt=""
                      aria-hidden="true"
                      className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${slideDir === "next" ? "proc-img-exit-left" : "proc-img-exit-right"}`}
                    />
                  )}
                  <img
                    key={activeStep}
                    src={processSteps[activeStep].image}
                    alt={processSteps[activeStep].title}
                    className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${prevStep !== null ? (slideDir === "next" ? "proc-img-enter-right" : "proc-img-enter-left") : ""}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Timeline dots */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
            <div className="flex items-start gap-0 overflow-x-auto pb-12 scrollbar-hide">
              {processSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`flex flex-col items-start flex-1 min-w-[90px] pr-3 transition-opacity duration-300 ${activeStep === i ? "opacity-100" : "opacity-40 hover:opacity-65"}`}
                >
                  <div className="relative w-full flex items-center mb-4" style={{ height: "14px" }}>
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/25" />
                    <span className={`relative z-10 block rounded-full border-2 transition-all duration-300 shrink-0 ${activeStep === i ? "w-3.5 h-3.5 bg-white border-white shadow-[0_0_8px_2px_rgba(255,255,255,0.45)]" : "w-2 h-2 bg-white/30 border-white/40"}`} />
                  </div>
                  <span className={`font-bold text-base sm:text-lg leading-none ${activeStep === i ? "text-white" : "text-white/70"}`}>{s.step}</span>
                  <span className={`text-xs mt-1.5 leading-snug text-left ${activeStep === i ? "text-white font-semibold" : "text-white/55"}`}>{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 4: EMPLOYER RESPONSIBILITIES ─────────────────────── */}
        <section className="w-full bg-gray-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Employer Responsibilities
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-4 max-w-3xl">
              Businesses sponsoring overseas workers may need to meet regulatory obligations under Australian immigration law.
            </p>
            <p className="text-sm text-slate-400 mb-12 max-w-3xl">
              Proper compliance supports smoother hiring, protects the business, and reduces risk during the migration process.
            </p>

            <div className="relative max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 px-0">
              <div className="size-[520px] -top-80 left-1/2 -translate-x-1/2 rounded-full absolute blur-[300px] -z-10 bg-indigo-100 pointer-events-none" />
              {employerResponsibilities.map((req, index) => {
                const cols = 4;
                const col = index % cols;
                const row = Math.floor(index / cols);
                const totalRows = Math.ceil(employerResponsibilities.length / cols);
                const isLastRow = row === totalRows - 1;
                const isLastCol = col === cols - 1;
                return (
                  <div
                    key={index}
                    style={{ transition: "background 0.2s ease, box-shadow 0.2s ease" }}
                    className={[
                      "group py-8 px-6 cursor-default rounded-xl",
                      "hover:bg-gradient-to-br hover:from-[#e0f0ff] hover:to-[#f0f7ff] hover:shadow-md",
                      !isLastRow ? "border-b border-gray-200" : "",
                      !isLastCol ? "lg:border-r lg:border-gray-200" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 mt-1 w-5 h-5 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                        <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <p className="text-[1rem] sm:text-[1.05rem] text-slate-600 leading-[1.75]">{req}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── SECTION 5: APPLICANT REQUIREMENTS ───────────────────────── */}
        <section className="w-full bg-white py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row-reverse items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/skills-assessment.jpg"
                alt="Applicant requirements for corporate immigration Australia"
              />
            </div>
            <div className="w-full max-w-lg">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Applicant Requirements
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6" />
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6">
                Overseas workers applying for employment visas may need to meet eligibility criteria depending on the visa type and nominated occupation.
              </p>
              <ul className="space-y-3">
                {applicantRequirements.map((req, i) => (
                  <li key={i} className="flex items-center gap-3 bg-indigo-50/60 rounded-xl px-4 py-3 border border-indigo-100">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="text-sm text-slate-700 font-medium">{req.label}</span>
                  </li>
                ))}
              </ul>

            </div>
          </div>
        </section>

        {/* ── SECTION 6: INDUSTRIES ─────────────────────────────────────── */}
        <section className="w-full bg-[#f8f9ff] py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3 text-center"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Industries That Use Corporate Immigration
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5 mx-auto" />
            <p className="text-base text-slate-500 mb-12 max-w-3xl text-center mx-auto">
              These sectors often rely on international talent to address skill shortages and support business growth across Australia.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {industries.map((ind, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all duration-200 p-5 flex flex-col items-center gap-3 text-center group cursor-default"
                >
                  <div className={`w-14 h-14 rounded-2xl ${ind.bg} flex items-center justify-center transition-transform duration-200 group-hover:scale-110`}>
                    {ind.icon}
                  </div>
                  <span className="text-sm font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">{ind.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 7: GUIDANCE ───────────────────────────────────── */}
        <section className="w-full bg-[#f0f4ff] py-20 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 items-start">

              {/* Left: heading + description */}
              <div className="w-full lg:max-w-sm lg:sticky lg:top-24 shrink-0">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 mb-5">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">How We Help</span>
                </div>
                <h2
                  className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-4 leading-snug"
                  style={{ fontFamily: "Poppins, sans-serif" }}
                >
                  Corporate Immigration Guidance
                </h2>
                <div className="w-16 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  Hiring international professionals requires knowledge of visa pathways, sponsorship rules, and workforce compliance.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  By planning workforce needs carefully and understanding immigration processes, businesses can make informed decisions when expanding their teams with global talent.
                </p>
              </div>

              {/* Right: clean service grid */}
              <div className="flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {supportServices.map((service, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all duration-200 px-5 py-4 flex items-center gap-4"
                    >
                      <span className="shrink-0 w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <p className="text-sm font-semibold text-slate-700">{service}</p>
                    </div>
                  ))}
                </div>

                {/* Info highlight box */}
                <div className="mt-5 bg-indigo-600 rounded-2xl p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="9" cy="7" r="4" stroke="white" strokeWidth="1.8" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-base leading-snug">
                      Expert guidance for Australian businesses hiring overseas talent
                    </p>
                    <p className="text-white/70 text-sm mt-1 leading-relaxed">
                      From visa pathway selection to sponsorship documentation, Hola World Migration supports your business at every step.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="w-full bg-gray-50 py-24 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left sticky */}
              <div className="lg:sticky lg:top-24">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Self Service Help</span>
                </div>
                <h2
                  className="text-4xl md:text-5xl font-black text-[#0a2540] mb-8"
                  style={{ fontFamily: "Poppins, sans-serif", lineHeight: "1.1" }}
                >
                  Frequently Asked <br /> Questions
                </h2>
                <p className="text-base text-gray-600 max-w-sm mb-6 leading-relaxed">
                  Have questions about corporate immigration and hiring overseas workers in Australia? Find answers below.
                </p>
                <div className="rounded-2xl overflow-hidden w-full max-w-sm">
                  <img
                    src="/assets/faq/faq-image.png"
                    alt="Corporate Immigration FAQ"
                    loading="lazy"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* Right: accordion */}
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-white hover:border-blue-300 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? -1 : i)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      aria-expanded={openFaqIndex === i}
                    >
                      <span className="text-base sm:text-lg font-semibold text-[#0a2540] pr-4">{faq.q}</span>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                        {openFaqIndex === i ? (
                          <Minus className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gray-200 mb-6" />
                        <p className="text-base text-gray-700 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ───────────────────────────────────────────────── */}
        <div className="py-8 px-6 md:px-24 bg-white border-t border-gray-100 w-full">
          <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto">
            <strong className="text-gray-500">Disclaimer:</strong> The information on this page is intended as general guidance only and does not constitute legal, migration, or professional advice. Visa requirements, eligibility criteria, sponsorship obligations, and processing times are subject to change by the Australian Government without notice. Always verify current requirements directly with the Department of Home Affairs or consult a registered migration agent (MARA) before making decisions about your business workforce migration strategy.
          </p>
        </div>

        <CTA02 />
      </main>

      <Footer />
    </div>
  );
}
